'use client'

import Script from 'next/script'
import * as React from 'react'

import { useIsLoggedInQuery, useUserIdQuery } from 'auth/hooks'
import {
  initScript as googleSiteTagInitScript,
  scriptUrl as googleSiteTagScriptUrl,
  globalSiteInit,
} from '@repo/analytics/google-site-tag'
import { iubendaConsentEventName } from '@repo/analytics/iubenda'
import { useIubendaConfig } from './iubenda'

export const GlobalSiteTag = ({ disableAds = false }: { disableAds?: boolean }) => {
  const { scriptProps, variant } = useIubendaConfig()
  const isIubendaManagedScript = scriptProps.type === 'text/plain'
  const deferredSourceProps = scriptProps['data-cf-defer']
    ? { 'data-cf-src': googleSiteTagScriptUrl }
    : {}

  const isLoggedInQuery = useIsLoggedInQuery()
  const userIdQuery = useUserIdQuery()
  const userId = isLoggedInQuery.data ? userIdQuery.data || null : null
  const initializedUserIdRef = React.useRef<string | null | undefined>(undefined)

  const initializeGlobalSiteTag = React.useCallback(() => {
    if (!isLoggedInQuery.isFetched || typeof window.gtag !== 'function') {
      return false
    }

    if (initializedUserIdRef.current !== userId) {
      globalSiteInit(userId)
      initializedUserIdRef.current = userId
    }

    return true
  }, [isLoggedInQuery.isFetched, userId])

  const handleGlobalSiteTagReady = React.useCallback(() => {
    initializeGlobalSiteTag()
  }, [initializeGlobalSiteTag])

  React.useEffect(() => {
    if (variant === 'locationNotSet' || initializeGlobalSiteTag()) {
      return
    }

    if (!isIubendaManagedScript) {
      return
    }

    const handleIubendaConsentEvent = () => {
      initializeGlobalSiteTag()
    }

    window.addEventListener(iubendaConsentEventName, handleIubendaConsentEvent)

    return () => {
      window.removeEventListener(iubendaConsentEventName, handleIubendaConsentEvent)
    }
  }, [initializeGlobalSiteTag, isIubendaManagedScript, variant])

  if (variant === 'locationNotSet') {
    return null
  }

  return (
    <>
      <Script
        id="global-site-tag-script-init"
        strategy="afterInteractive"
        onReady={isIubendaManagedScript ? undefined : handleGlobalSiteTagReady}
        {...scriptProps}
      >
        {googleSiteTagInitScript({ disableAds })}
      </Script>
      <Script
        id="global-site-tag-script"
        src={googleSiteTagScriptUrl}
        strategy="afterInteractive"
        {...scriptProps}
        {...deferredSourceProps}
      />
    </>
  )
}
